import {
  API_TYPES,
  WorkerRequirementDomain,
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import {
  requirementRelationship,
  useGetWorkerRequirements,
} from "@src/appV2/redesign/Documents/api/useGetWorkerRequirements";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";

export interface NCOrientationRequirementMapping {
  id: string;
  type: "requirement-mappings";
  attributes: {
    requirementId: string;
    level: WorkerRequirementMappingLevel;
    qualifications: string[];
    workplaceTypes: string[];
    requiredBy: string[];
    requiredForBooking: boolean;
    domain: WorkerRequirementDomain;
    isOrientationRequirement?: boolean;
  };
}

export interface NCOrientationRequirement extends WorkerRequirement {
  requirementMappings?: NCOrientationRequirementMapping[];
}

interface UseNCOrientationStatusResult {
  shouldShow: boolean;
  shouldShowForBooking: boolean;
  isLoading: boolean;
  orientationRequirements?: NCOrientationRequirement[];
  allCompleted?: boolean;
  completedCount: number;
  totalCount: number;
}

export function useNCOrientationStatus(): UseNCOrientationStatusResult {
  const isEnabled = useCbhFlag(CbhFeatureFlag.NC_ORIENTATION, { defaultValue: false });
  const worker = useDefinedWorker();
  const isNC = worker.address?.stateCode === "NC";

  const { data: qualifications } = useWorkerQualifications();

  const qualificationNames = qualifications.map((q) => q.attributes.name);

  const { data: workerRequirements, isLoading } = useGetWorkerRequirements(
    {
      filter: {
        qualifications: qualificationNames,
        levels: [WorkerRequirementMappingLevel.STATE],
        requiredForBooking: "",
      },
      include: [...requirementRelationship, API_TYPES.requirementMappings],
    },
    { enabled: isEnabled && isNC && qualificationNames.length > 0 }
  );

  if (!isEnabled || !isNC) {
    return {
      shouldShow: false,
      shouldShowForBooking: false,
      isLoading: false,
      completedCount: 0,
      totalCount: 0,
    };
  }

  const orientationRequirements = (
    workerRequirements as NCOrientationRequirement[] | undefined
  )?.filter(
    (requirement) =>
      isDefined(requirement.requirementMappings) &&
      requirement.requirementMappings.some((m) => m.attributes.isOrientationRequirement === true)
  );

  if (!isDefined(orientationRequirements) || orientationRequirements.length === 0) {
    return {
      shouldShow: false,
      shouldShowForBooking: false,
      isLoading,
      completedCount: 0,
      totalCount: 0,
    };
  }

  const completedCount = orientationRequirements.filter(
    (requirement) => requirement.status === WorkerRequirementStatus.APPROVED
  ).length;
  const totalCount = orientationRequirements.length;
  const allCompleted = completedCount === totalCount;

  const hasIncompleteBookingRequirement = orientationRequirements.some(
    (requirement) =>
      requirement.status !== WorkerRequirementStatus.APPROVED &&
      requirement.requirementMappings?.some(
        (mapping) => mapping.attributes.requiredForBooking === true
      )
  );

  return {
    shouldShow: !allCompleted,
    shouldShowForBooking: hasIncompleteBookingRequirement,
    isLoading,
    orientationRequirements,
    allCompleted,
    completedCount,
    totalCount,
  };
}
